<script setup lang="ts">
import * as Sentry from '@sentry/nuxt'
import { useI18n } from 'vue-i18n'
import { getChargemapUrl } from '~~/app/utils/url'

const props = defineProps({
    error: {
        type: Object,
        default: () => ({})
    }
})

const config = useRuntimeConfig()
const { t } = useI18n()
const cdnUrl = useRuntimeConfig().app.cdnURL

const getBackHomeTranslation = t('error.default.back')
const getImageAltTranslation = t('error.default.image_alt')

onMounted(() => {
    if (props.error?.statusCode && props.error.statusCode !== 404) {
        Sentry.captureException(new Error(props.error.stack))
    }
})

const redirectToChargemap = () => {
    if (window.location !== window?.parent?.location) {
        window.top?.postMessage(
            { action: 'openUrl', url: '' },
            document.referrer
        )
    } else {
        return navigateTo(getChargemapUrl(), { external: true })
    }
}

const getCorrespondingTranslation = (resource: string): string => {
    if ([404, 500].includes(props.error?.statusCode)) {
        return t(`error.${props.error.statusCode}.${resource}`)
    }

    return t(`error.default.${resource}`)
}

useSeoMeta({
    title: getCorrespondingTranslation('title'),
    ogTitle: getCorrespondingTranslation('title'),
    description: getCorrespondingTranslation('meta_description'),
    ogDescription: getCorrespondingTranslation('meta_description'),
    robots: { none: true }
})
</script>

<template>
    <NuxtLayout name="seo">
        <div
            class="bg-white size-full flex flex-col md:flex-row items-center md:items-start justify-center py-8 md:py-24 px-5 mx-auto lg:w-5/6"
        >
            <NuxtImg
                width="160"
                height="150"
                class="px-4 w-1/2 md:w-1/3 max-w-[400px]"
                :alt="getImageAltTranslation"
                :src="`${cdnUrl}/images/Route.svg`"
            />

            <div class="flex flex-col items-start gap-5 px-4">
                <h1 class="text-4xl font-semibold text-left">
                    {{ getCorrespondingTranslation('title_text') }}
                </h1>
                <h2 class="text-2xl font-normal text-left">
                    {{ getCorrespondingTranslation('text') }}
                </h2>
                <div
                    v-if="config.public.NODE_ENV !== 'production'"
                    class="border rounded-2xl p-4 max-w-80 md:max-w-2xl"
                >
                    <div class="truncate text-wrap">
                        Message: {{ error.statusMessage }}
                    </div>
                    <br />
                    <div>Stacktrace: {{ error.stack }}</div>
                </div>
                <ChargemapB2CButton
                    type="primary"
                    size="large"
                    @click="redirectToChargemap"
                >
                    {{ getBackHomeTranslation }}
                </ChargemapB2CButton>
            </div>
        </div>
    </NuxtLayout>
</template>
